/**
 * AddMemberModal Component
 *
 * Модальное окно для добавления организации в группу компаний.
 * С поиском по названию.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Box,
  ScrollArea,
  Loader,
  Center,
  Badge,
} from '@mantine/core';
import {
  IconSearch,
  IconBuilding,
  IconPlus,
  IconMapPin,
} from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import tokens from '../../../theme/designTokens';

// Конфигурация badge-ей для этапов воронки
const STAGE_BADGES = {
  'new': { color: '#0176D3', label: 'Новый' },
  'message_sent': { color: '#7DC681', label: 'Сообщ.' },
  'call': { color: '#5EBA62', label: 'Звонок' },
  'meeting': { color: '#48A84C', label: 'Встреча' },
  'master_class': { color: '#3D9141', label: 'МК' },
  'contract_signed': { color: '#2E7D32', label: 'Договор' },
  'classes_active': { color: '#1B5E20', label: 'Занятия' },
  'classes_paused': { color: '#FFA000', label: 'Пауза' },
  'contract_terminated': { color: '#C62828', label: 'Растор.' },
};

/**
 * API функция для поиска доступных организаций
 */
const searchAvailableOrganizations = async (groupId, search = '') => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('limit', '20');

  const response = await fetch(
    `/api/company-groups/${groupId}/available-organizations?${params}`,
    { credentials: 'include' }
  );

  if (!response.ok) {
    throw new Error('Не удалось загрузить организации');
  }

  return response.json();
};

/**
 * Компонент отображения организации в списке
 */
const OrganizationItem = ({ organization, onSelect, loading }) => {
  const stageBadge = STAGE_BADGES[organization.cooperation_stage] || {
    color: '#999999',
    label: organization.cooperation_stage || '—'
  };

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[2]}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[1]}
      onClick={() => !loading && onSelect(organization)}
    >
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {organization.name}
          </Text>
          <Badge
            variant="filled"
            size="xs"
            radius="xl"
            styles={{
              root: {
                backgroundColor: stageBadge.color,
                color: 'white',
              }
            }}
          >
            {stageBadge.label}
          </Badge>
        </Group>

        <Text size="xs" c="dimmed" truncate>
          {organization.category}
        </Text>

        {organization.address && (
          <Group gap={4} mt={4}>
            <IconMapPin size={12} color={tokens.colors.neutral[5]} />
            <Text size="xs" c="dimmed" truncate>
              {organization.address}
            </Text>
          </Group>
        )}
      </Box>

      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        loading={loading}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(organization);
        }}
      >
        Добавить
      </Button>
    </Group>
  );
};

/**
 * Основной компонент модального окна
 */
const AddMemberModal = ({ opened, onClose, onAdd, groupId }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [organizations, setOrganizations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [error, setError] = useState('');

  // Загрузка организаций
  const loadOrganizations = useCallback(async () => {
    if (!groupId) return;

    try {
      setLoading(true);
      setError('');
      const data = await searchAvailableOrganizations(groupId, debouncedSearch);
      setOrganizations(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load organizations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [groupId, debouncedSearch]);

  useEffect(() => {
    if (opened) {
      loadOrganizations();
    }
  }, [opened, loadOrganizations]);

  // Сброс при закрытии
  useEffect(() => {
    if (!opened) {
      setSearch('');
      setOrganizations([]);
      setError('');
    }
  }, [opened]);

  // Добавить организацию
  const handleSelect = async (organization) => {
    setAddingId(organization.id);
    setError('');

    try {
      await onAdd(organization.id);
      // После успешного добавления перезагружаем список
      await loadOrganizations();
    } catch (err) {
      console.error('Failed to add organization:', err);
      setError(err.message);
    } finally {
      setAddingId(null);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconBuilding size={20} color={tokens.colors.brand.primary} />
          <Text fw={600}>Добавить организацию в группу</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <TextInput
          placeholder="Поиск по названию..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Text size="xs" c="dimmed">
          Доступно для добавления: {total}
        </Text>

        <ScrollArea h={400}>
          {loading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : organizations.length > 0 ? (
            <Stack gap="xs">
              {organizations.map((org) => (
                <OrganizationItem
                  key={org.id}
                  organization={org}
                  onSelect={handleSelect}
                  loading={addingId === org.id}
                />
              ))}
            </Stack>
          ) : (
            <Box py="xl" style={{ textAlign: 'center' }}>
              <IconBuilding
                size={40}
                style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
              />
              <Text size="sm" c="dimmed">
                {search ? 'Организации не найдены' : 'Нет доступных организаций'}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                {search
                  ? 'Попробуйте изменить поисковый запрос'
                  : 'Все организации уже в группах или являются группами'
                }
              </Text>
            </Box>
          )}
        </ScrollArea>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Закрыть
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default AddMemberModal;
